import type { ConsentState } from '../types/cookies.types';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

let gaInitialized = false;
let fbInitialized = false;

/**
 * Loads Google Analytics only when analytics consent is granted.
 */
export function initGoogleAnalytics(trackingId: string): void {
  if (!trackingId || gaInitialized || typeof window === 'undefined') {
    return;
  }
  gaInitialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', trackingId, { anonymize_ip: true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(trackingId)}`;
  document.head.appendChild(script);
}

/**
 * Loads Meta Pixel when marketing consent is granted.
 */
export function initFacebookPixel(pixelId: string): void {
  if (!pixelId || fbInitialized || typeof window === 'undefined') {
    return;
  }
  fbInitialized = true;

  const fbq = function fbq(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as typeof window.fbq & {
    callMethod?: (...a: unknown[]) => void;
    queue: unknown[];
    loaded?: boolean;
    version?: string;
    push?: typeof fbq;
  };

  if (!window.fbq) {
    window.fbq = fbq;
  }
  if (!window._fbq) {
    window._fbq = window.fbq;
  }
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

/**
 * Applies third-party scripts based on current consent.
 */
export function applyThirdPartyScripts(
  consent: ConsentState,
  options: { gaTrackingId?: string; facebookPixelId?: string },
): void {
  if (consent.analytics && options.gaTrackingId) {
    initGoogleAnalytics(options.gaTrackingId);
  }
  if (consent.marketing && options.facebookPixelId) {
    initFacebookPixel(options.facebookPixelId);
  }
}
